import React, { useEffect, useRef, useState, useMemo } from 'react';
import GameOverModal from './GameOverModal';
import WinModal from './WinModal';
import PropTypes from 'prop-types';
import LoadingSpinner from './LoadingSpinner';
import FPSCounter from './FPSCounter'; // Import FPSCounter
import { debounce } from 'lodash'; // Ensure lodash is installed, or use custom debounce below

// Custom debounce if lodash is not used
// function debounce(func, wait) {
//   let timeout;
//   return function executedFunction(...args) {
//     const later = () => {
//       clearTimeout(timeout);
//       func(...args);
//     };
//     clearTimeout(timeout);
//     timeout = setTimeout(later, wait);
//   };
// }

// Global image cache
const imageCache = new Map();

const preloadImages = (imageSources) => {
  return Promise.all(
    imageSources.map((src) => {
      if (imageCache.has(src)) {
        return Promise.resolve(imageCache.get(src));
      }
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.src = src;
        img.onload = () => {
          imageCache.set(src, img);
          resolve(img);
        };
        img.onerror = () => {
          console.error(`Failed to load image: ${src}`);
          reject(new Error(`Failed to load image: ${src}`));
        };
      });
    })
  );
};

function Game({ onGameOver, musicSettings, level = 1 }) {
  const canvasRef = useRef(null);
  const audioRef = useRef(null);
  const dieSfxRef = useRef(null);
  const jumpSfxRef = useRef(null);
  const springSfxRef = useRef(null);
  const nexoJumpSfxRef = useRef(null);
  const bgOffsetRef = useRef(0);
  const [progress, setProgress] = useState(0);
  const [gameStatus, setGameStatus] = useState('loading');
  const [shatterAnimation, setShatterAnimation] = useState(false);
  const [error, setError] = useState(null);
  const [failedImages, setFailedImages] = useState([]);
  const [isAudioReady, setIsAudioReady] = useState(false);
  const [isImagesReady, setIsImagesReady] = useState(false);
  const [visibleObjects, setVisibleObjects] = useState([]);
  const [resetTrigger, setResetTrigger] = useState(0);
  const [levelData, setLevelData] = useState(null);

  const gridSize = 64;
  const jumpVelocity = 0.23;
  const springJumpVelocity = jumpVelocity * 1.5;
  const nexoJumpVelocity = 0.3;
  const playerSpeed = 0.10;
  const gravity = 0.017;
  const canvasHeight = 6;
  const canvasWidth = 10;
  const visibleRange = 4;
  const FALLBACK_MUSIC = 'Rex Hooligan - You Get Me High.mp3';

  const safeMusicSettings = useMemo(() => musicSettings || {
    musicVolume: 0.5,
    sfxVolume: 0.5,
    isMusicEnabled: true,
    isSfxEnabled: true,
    isFpsCounterEnabled: false, // Added FPS counter setting
  }, [musicSettings]);

  useEffect(() => {
    if (!musicSettings) {
      console.warn('musicSettings is undefined, using fallback settings');
    }
  }, [musicSettings]);

  useEffect(() => {
    if (!Number.isInteger(level) || level < 1) {
      console.warn(`Invalid level prop: ${level}. Defaulting to level 1.`);
      setError(`Invalid level: ${level}. Please select a valid level.`);
      setIsAudioReady(true);
      setIsImagesReady(true);
      setGameStatus('waiting');
    }
  }, [level]);

  useEffect(() => {
    if (!Number.isInteger(level) || level < 1) return;

    setGameStatus('loading');
    setLevelData(null);

    import(`../assets/levels/level${level}.json`)
      .then((module) => {
        setLevelData(module.default);
      })
      .catch((err) => {
        setError(`Failed to load level ${level}: ${err.message}`);
        setIsAudioReady(true);
        setIsImagesReady(true);
        setGameStatus('waiting');
      });
  }, [level, resetTrigger]);

  const gameObjects = useMemo(() => levelData?.objects || [], [levelData]);

  // Audio preloading with AudioContext
  useEffect(() => {
    if (!levelData) return;

    const audioContext = new AudioContext();
    const audioSources = [
      { ref: audioRef, src: levelData.music ? encodeURI(levelData.music) : encodeURI(FALLBACK_MUSIC), type: 'music' },
      { ref: dieSfxRef, src: '/die.mp3', type: 'die SFX' },
      { ref: jumpSfxRef, src: '/jump.mp3', type: 'jump SFX' },
      { ref: springSfxRef, src: '/spring.mp3', type: 'spring SFX' },
      { ref: nexoJumpSfxRef, src: '/nexoJump.mp3', type: 'nexoJump SFX' },
    ];

    let loadedCount = 0;
    const totalAudios = audioSources.length;

    audioSources.forEach(({ ref, src, type }) => {
      ref.current = new Audio();
      fetch(src)
        .then((response) => response.arrayBuffer())
        .then((buffer) => audioContext.decodeAudioData(buffer))
        .then(() => {
          ref.current.src = src;
          ref.current.preload = 'auto';
          ref.current.oncanplay = () => {
            loadedCount += 1;
            if (loadedCount === totalAudios) {
              setIsAudioReady(true);
            }
          };
          ref.current.onerror = () => {
            setError(`Failed to load ${type} audio (${src})`);
            loadedCount += 1;
            if (loadedCount === totalAudios) {
              setIsAudioReady(true);
            }
          };
          ref.current.load();
        })
        .catch((err) => {
          setError(`Failed to decode ${type} audio: ${err.message}`);
          loadedCount += 1;
          if (loadedCount === totalAudios) {
            setIsAudioReady(true);
          }
        });
    });

    return () => {
      audioSources.forEach(({ ref }) => {
        if (ref.current) {
          ref.current.pause();
          ref.current.currentTime = 0;
        }
      });
      audioContext.close();
    };
  }, [levelData, resetTrigger]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = safeMusicSettings.musicVolume || 0.5;
      audioRef.current.muted = !safeMusicSettings.isMusicEnabled;
    }
    [dieSfxRef, jumpSfxRef, springSfxRef, nexoJumpSfxRef].forEach((sfxRef) => {
      if (sfxRef.current) {
        sfxRef.current.volume = safeMusicSettings.sfxVolume || 0.5;
        sfxRef.current.muted = !safeMusicSettings.isSfxEnabled;
      }
    });
  }, [safeMusicSettings]);

  useEffect(() => {
    if (isAudioReady && isImagesReady && levelData && gameStatus === 'loading') {
      setGameStatus('waiting');
    }
  }, [isAudioReady, isImagesReady, levelData, gameStatus]);

  useEffect(() => {
    const chunkSize = 50;
    let loadedObjects = [];
    let currentIndex = 0;

    const loadChunk = () => {
      const nextIndex = Math.min(currentIndex + chunkSize, gameObjects.length);
      loadedObjects = [
        ...loadedObjects,
        ...gameObjects.slice(currentIndex, nextIndex),
      ];
      setVisibleObjects(loadedObjects);
      currentIndex = nextIndex;

      if (currentIndex < gameObjects.length) {
        requestAnimationFrame(loadChunk);
      }
    };

    if (gameObjects.length > 0) {
      loadChunk();
    }
  }, [gameObjects, resetTrigger]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      setError('Canvas not found');
      return;
    }
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      setError('Failed to get 2D context');
      return;
    }

    let animationFrameId;
    const targetFrameTime = 1000 / 60;
    let lastTime = 0;
    let accumulatedTime = 0;
    let frameCount = 0;
    let lastFpsTime = performance.now();

    let player = {
      x: 2,
      y: 1,
      vy: 0,
      isJumping: false,
      isSpringJumping: false,
      isNexoJumping: false,
      rotation: 0,
      targetRotation: 0,
      canJumpFromNexo: true,
    };
    let scrollOffset = 0;
    let cogs = gameObjects
      .filter((obj) => obj.type === 'cog')
      .map((obj) => ({
        x: obj.x1,
        y: obj.y1,
        x1: obj.x1,
        y1: obj.y1,
        x2: obj.x2,
        y2: obj.y2,
        speed: obj.speed,
        t: 0,
        forward: true,
        rotation: 0,
      }));
    let nexoJumps = gameObjects
      .filter((obj) => obj.type === 'nexoJump')
      .map((obj) => ({
        x: obj.x,
        y: obj.y,
        rotation: 0,
      }));

    const updateCanvasSize = () => {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      canvas.width = viewportWidth;
      canvas.height = viewportHeight;
      const scale = Math.min(viewportWidth / (canvasWidth * gridSize), viewportHeight / (canvasHeight * gridSize));
      return scale;
    };

    const handleResize = debounce(() => {
      scale = updateCanvasSize();
    }, 300);

    let scale = updateCanvasSize();
    window.addEventListener('resize', handleResize);

    const imageSources = [
      '/player1.png',
      '/player1spring.png',
      '/player1oh.png',
      '/bg2.jpg',
      '/blueBlock.png',
      '/orangeBlock.png',
      '/spike.png',
      '/shatteredPlayer1.png',
      '/cog.png',
      '/spring.png',
      '/star.png',
      '/nexoJump.png',
    ];

    preloadImages(imageSources)
      .then(() => {
        setIsImagesReady(true);
      })
      .catch((err) => {
        setError('Image loading error');
        setIsImagesReady(true);
        setFailedImages((prev) => [...prev, err.message.split(': ')[1]]);
      });

    const snapToNearest90Degrees = (rotation) => {
      const ninetyDegrees = Math.PI / 2;
      return Math.round(rotation / ninetyDegrees) * ninetyDegrees;
    };

    const snapToSurface = (playerX, playerY, currentRotation, previousY) => {
      let newY = playerY;
      let onSurface = false;

      visibleObjects.forEach((obj) => {
        if (obj.type === 'block' || obj.type === 'spring') {
          const objLeft = obj.x - scrollOffset;
          const objRight = objLeft + 1;
          const objTop = obj.y;

          if (
            playerX < objRight &&
            playerX + 1 > objLeft &&
            previousY > objTop &&
            playerY >= objTop - 0.2 &&
            playerY <= objTop + 1.2
          ) {
            newY = objTop + 1;
            onSurface = true;
          }
        }
      });

      if (!onSurface && newY <= 0) {
        newY = 1;
        onSurface = true;
      }

      const targetRotation = onSurface ? snapToNearest90Degrees(currentRotation) : currentRotation;

      if (onSurface) {
        player.isSpringJumping = false;
        player.isNexoJumping = false;
        player.canJumpFromNexo = true;
      }

      return { newY, onSurface, targetRotation };
    };

    player.y = snapToSurface(player.x, player.y, player.rotation).newY;
    player.targetRotation = snapToNearest90Degrees(player.rotation);

    const playSound = (sfxRef) => {
      if (safeMusicSettings.isSfxEnabled && sfxRef.current) {
        sfxRef.current.currentTime = 0;
        sfxRef.current.play().catch((err) => {
          console.error(`Failed to play sound: ${err.message}`);
        });
      }
    };

    const draw = () => {
      if (gameStatus === 'loading') {
        return;
      }

      try {
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        const bgImage = imageCache.get('/bg2.jpg');
        if (!bgImage || !bgImage.complete || bgImage.naturalWidth === 0) {
          ctx.fillStyle = 'gray';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
        } else {
          const bgWidth = canvas.width;
          const overlap = 6;
          let offset = (bgOffsetRef.current * canvas.width) % bgWidth;
          if (offset > 0) offset -= bgWidth;
          const bgX1 = offset;
          const bgX2 = offset + bgWidth - overlap;
          ctx.drawImage(bgImage, bgX1, 0, bgWidth, canvas.height);
          ctx.drawImage(bgImage, bgX2, 0, bgWidth, canvas.height);
        }

        const visibleObjectsToRender = visibleObjects.filter((obj) => {
          const objX = obj.type === 'cog' ? Math.min(obj.x1, obj.x2) : obj.x;
          const objRight = objX + 1;
          return (
            obj.type !== 'nexoJump' &&
            objX - scrollOffset < canvasWidth + visibleRange &&
            objRight - scrollOffset > -visibleRange
          );
        });

        visibleObjectsToRender.forEach((obj) => {
          if (obj.type === 'cog') {
            const x1 = (obj.x1 - scrollOffset + 0.5) * gridSize * scale;
            const y1 = (canvasHeight - obj.y1 - 0.5) * gridSize * scale;
            const x2 = (obj.x2 - scrollOffset + 0.5) * gridSize * scale;
            const y2 = (canvasHeight - obj.y2 - 0.5) * gridSize * scale;
            ctx.beginPath();
            ctx.strokeStyle = '#00f0ff';
            ctx.lineWidth = 4 * scale;
            ctx.moveTo(x1, y1);
            ctx.lineTo(x2, y2);
            ctx.stroke();
          } else {
            const imgKey = obj.type === 'block' ? obj.color + 'Block' : obj.type;
            const img = imageCache.get(`/${imgKey}.png`);
            const x = (obj.x - scrollOffset) * gridSize * scale;
            const y = (canvasHeight - obj.y - 1) * gridSize * scale;
            if (
              img &&
              img.complete &&
              img.naturalWidth !== 0 &&
              x > -gridSize * scale &&
              x < canvas.width
            ) {
              ctx.drawImage(img, x, y, gridSize * scale, gridSize * scale);
            }
          }
        });

        cogs.forEach((cog) => {
          const x = (cog.x - scrollOffset) * gridSize * scale;
          const y = (canvasHeight - cog.y - 1) * gridSize * scale;
          if (
            imageCache.get('/cog.png')?.complete &&
            imageCache.get('/cog.png')?.naturalWidth !== 0 &&
            x > -gridSize * scale &&
            x < canvas.width
          ) {
            ctx.save();
            ctx.translate(x + (gridSize * scale) / 2, y + (gridSize * scale) / 2);
            ctx.rotate(cog.rotation);
            ctx.drawImage(
              imageCache.get('/cog.png'),
              -(gridSize * scale) / 2,
              -(gridSize * scale) / 2,
              gridSize * scale,
              gridSize * scale
            );
            ctx.restore();
          }
        });

        nexoJumps.forEach((nexo) => {
          const x = (nexo.x - scrollOffset) * gridSize * scale;
          const y = (canvasHeight - nexo.y - 1) * gridSize * scale;
          if (
            imageCache.get('/nexoJump.png')?.complete &&
            imageCache.get('/nexoJump.png')?.naturalWidth !== 0 &&
            x > -gridSize * scale &&
            x < canvas.width
          ) {
            ctx.save();
            ctx.translate(x + (gridSize * scale) / 2, y + (gridSize * scale) / 2);
            ctx.rotate(nexo.rotation);
            ctx.drawImage(
              imageCache.get('/nexoJump.png'),
              -(gridSize * scale) / 2,
              -(gridSize * scale) / 2,
              gridSize * scale,
              gridSize * scale
            );
            ctx.restore();
          }
        });

        const playerX = player.x * gridSize * scale;
        const playerY = (canvasHeight - player.y - 1) * gridSize * scale;
        const playerImg = shatterAnimation
          ? imageCache.get('/shatteredPlayer1.png')
          : player.isSpringJumping
          ? imageCache.get('/player1spring.png')
          : player.isNexoJumping
          ? imageCache.get('/player1oh.png')
          : imageCache.get('/player1.png');
        if (playerImg?.complete && playerImg.naturalWidth !== 0) {
          ctx.save();
          ctx.translate(playerX + (gridSize * scale) / 2, playerY + (gridSize * scale) / 2);
          ctx.rotate(player.rotation);
          ctx.drawImage(
            playerImg,
            -(gridSize * scale) / 2,
            -(gridSize * scale) / 2,
            gridSize * scale,
            gridSize * scale
          );
          ctx.restore();
        }

        if (gameStatus === 'waiting') {
          ctx.fillStyle = 'white';
          ctx.font = `bold ${40 * scale}px Arial`;
          ctx.textAlign = 'center';
          ctx.fillText('Tap to start', canvas.width / 2, canvas.height / 2);
        }

        if (failedImages.length > 0) {
          ctx.fillStyle = 'red';
          ctx.font = `bold ${32 * scale}px Arial`;
          ctx.textAlign = 'left';
          ctx.fillText('Failed to load:', 20, 60);
          failedImages.forEach((img, index) => {
            ctx.fillText(img.split('/').pop(), 20, 100 + index * 40);
          });
        }
      } catch (err) {
        setError('Rendering failed');
      }
    };

    const update = (delta) => {
      const deltaSeconds = delta / 1000;

      if (gameStatus !== 'playing') return;

      let newY = player.y;
      let newVy = player.vy;
      scrollOffset += playerSpeed * deltaSeconds * 60;
      bgOffsetRef.current -= 0.0005 * deltaSeconds * 60;
      if (bgOffsetRef.current <= -1) {
        bgOffsetRef.current += 1;
      }

      cogs.forEach((cog) => {
        const cogX = cog.x - scrollOffset;
        if (cogX > -visibleRange && cogX < canvasWidth + visibleRange) {
          if (cog.forward) {
            cog.t += cog.speed * deltaSeconds * 60;
            if (cog.t >= 1) {
              cog.t = 1;
              cog.forward = false;
            }
          } else {
            cog.t -= cog.speed * deltaSeconds * 60;
            if (cog.t <= 0) {
              cog.t = 0;
              cog.forward = true;
            }
          }
          cog.x = cog.x1 + (cog.x2 - cog.x1) * cog.t;
          cog.y = cog.y1 + (cog.y2 - cog.y1) * cog.t;
          cog.rotation += deltaSeconds * (2 * Math.PI) / 1;
          cog.rotation %= 2 * Math.PI;
        }
      });

      nexoJumps.forEach((nexo) => {
        const nexoX = nexo.x - scrollOffset;
        if (nexoX > -visibleRange && nexoX < canvasWidth + visibleRange) {
          nexo.rotation -= deltaSeconds * (2 * Math.PI) / 2;
          nexo.rotation %= 2 * Math.PI;
        }
      });

      newY += newVy * deltaSeconds * 60;
      newVy -= gravity * deltaSeconds * 60;

      const { newY: snappedY, onSurface, targetRotation } = snapToSurface(player.x, newY, player.rotation, player.y);

      if (onSurface) {
        newY = snappedY;
        newVy = 0;
        player.isJumping = false;
        player.targetRotation = targetRotation;
      } else {
        player.isJumping = true;
      }

      if (player.isJumping) {
        const rotationSpeed = (Math.PI / 2) / 0.5;
        const rotationDelta = rotationSpeed * deltaSeconds;
        if (player.rotation < player.targetRotation) {
          player.rotation = Math.min(
            player.rotation + rotationDelta,
            player.targetRotation
          );
        } else if (player.rotation > player.targetRotation) {
          player.rotation = Math.max(
            player.rotation - rotationDelta,
            player.targetRotation
          );
        }
      } else {
        player.rotation = player.targetRotation;
      }

      if (newY < 0) {
        setShatterAnimation(true);
        setGameStatus('gameover');
        playSound(dieSfxRef);
        setTimeout(() => {
          setShatterAnimation(false);
        }, 500);
        return;
      }

      visibleObjects.forEach((obj) => {
        if (obj.type === 'block') {
          const objLeft = obj.x - scrollOffset;
          const objRight = objLeft + 1;
          const objTop = obj.y;
          const objBottom = objTop + 1;
          const playerLeft = player.x;
          const playerRight = playerLeft + 1;
          const playerTop = newY;
          const playerBottom = newY + 1;

          if (
            playerLeft < objRight &&
            playerRight > objLeft &&
            playerTop < objBottom &&
            playerBottom > objTop
          ) {
            if (player.y > objTop) {
              return;
            } else {
              setShatterAnimation(true);
              setGameStatus('gameover');
              playSound(dieSfxRef);
              setTimeout(() => {
                setShatterAnimation(false);
              }, 500);
              return;
            }
          }
        }
      });

      visibleObjects.forEach((obj) => {
        const objLeft = obj.x - scrollOffset;
        const objRight = objLeft + 1;
        const objTop = obj.y;
        const playerLeft = player.x;
        const playerRight = playerLeft + 1;
        const playerBottom = newY;

        const isOnAddon = obj.type === 'spring' &&
          playerLeft < objRight &&
          playerRight > objLeft &&
          playerBottom >= objTop - 0.2 &&
          playerBottom <= objTop + 1.2;

        if (isOnAddon) {
          newY = objTop + 1;
          newVy = springJumpVelocity;
          player.isJumping = true;
          player.isSpringJumping = true;
          playSound(springSfxRef);
          player.targetRotation += Math.PI / 2;
          return;
        }

        if (
          playerLeft < objRight &&
          playerRight > objLeft &&
          playerBottom >= objTop - 0.2 &&
          playerBottom <= objTop + 1
        ) {
          if (obj.type === 'spike') {
            const spikeBaseY = objTop;
            const spikeTopY = objTop + 1;
            const spikeCenterX = objLeft + 0.5;

            if (playerBottom >= spikeBaseY && playerBottom <= spikeTopY) {
              const playerCenterX = playerLeft + 0.5;
              const relativeY = (playerBottom - spikeBaseY) / (spikeTopY - spikeBaseY);
              const triangleHalfWidth = 0.5 * (1 - relativeY);
              const distanceX = Math.abs(playerCenterX - spikeCenterX);
              if (distanceX <= triangleHalfWidth) {
                setShatterAnimation(true);
                setGameStatus('gameover');
                playSound(dieSfxRef);
                setTimeout(() => {
                  setShatterAnimation(false);
                }, 500);
                return;
              }
            }
          } else if (obj.type === 'star') {
            const starCenterX = objLeft + 0.5;
            const starCenterY = objTop + 0.5;
            const playerCenterX = playerLeft + 0.5;
            const playerCenterY = newY + 0.5;
            const hitboxRadius = 0.5;

            const distance = Math.sqrt(
              (starCenterX - playerCenterX) ** 2 + (starCenterY - playerCenterY) ** 2
            );

            if (distance <= hitboxRadius) {
              setGameStatus('win');
              const completedLevels = JSON.parse(localStorage.getItem('completedLevels')) || Array(10).fill(false);
              completedLevels[level - 1] = true;
              localStorage.setItem('completedLevels', JSON.stringify(completedLevels));
              return;
            }
          }
        }
      });

      cogs.forEach((cog) => {
        const cogCenterX = (cog.x - scrollOffset + 0.5) * gridSize;
        const cogCenterY = (canvasHeight - cog.y - 0.5) * gridSize;
        const playerCenterX = (player.x + 0.5) * gridSize;
        const playerCenterY = (canvasHeight - newY - 0.5) * gridSize;
        const hitboxRadius = 48;

        const distance = Math.sqrt(
          (cogCenterX - playerCenterX) ** 2 + (cogCenterY - playerCenterY) ** 2
        );

        if (distance <= hitboxRadius) {
          setShatterAnimation(true);
          setGameStatus('gameover');
          playSound(dieSfxRef);
          setTimeout(() => {
            setShatterAnimation(false);
          }, 500);
          return;
        }
      });

      player.y = newY;
      player.vy = newVy;
    };

    const handleTouch = (e) => {
      e.preventDefault();

      if (gameStatus === 'waiting' && isAudioReady && isImagesReady) {
        setGameStatus('playing');
        if (safeMusicSettings.isMusicEnabled && audioRef.current && audioRef.current.paused) {
          audioRef.current.volume = safeMusicSettings.musicVolume || 0.5;
          audioRef.current.play().catch((err) => {
            setError(`Audio playback failed (${audioRef.current.src}): ${err.message}`);
          });
        }
      } else if (gameStatus === 'playing') {
        let canJump = !player.isJumping;
        let isNexoJump = false;

        visibleObjects.forEach((obj) => {
          if (obj.type === 'nexoJump') {
            const objCenterX = obj.x - scrollOffset + 0.5;
            const objCenterY = obj.y + 0.5;
            const playerCenterX = player.x + 0.5;
            const playerCenterY = player.y + 0.5;
            const hitboxRadius = 0.7;

            const distance = Math.sqrt(
              (objCenterX - playerCenterX) ** 2 + (objCenterY - playerCenterY) ** 2
            );

            if (distance <= hitboxRadius) {
              console.log('nexoJump triggered!');
              isNexoJump = true;
              player.vy = nexoJumpVelocity;
              player.isJumping = true;
              player.isNexoJumping = true;
              player.isSpringJumping = false;
              player.canJumpFromNexo = true;
              player.targetRotation += Math.PI / 2;
              playSound(nexoJumpSfxRef);
            }
          }
        });

        if (!isNexoJump && canJump) {
          console.log('Regular jump triggered');
          player.vy = jumpVelocity;
          player.isJumping = true;
          player.isSpringJumping = false;
          player.isNexoJumping = false;
          player.targetRotation += Math.PI / 2;
          playSound(jumpSfxRef);
        }
      }
    };

    canvas.addEventListener('touchstart', handleTouch, { passive: false });
    canvas.addEventListener('click', handleTouch, { passive: false });

    const calculateProgress = () => {
      const star = gameObjects.find((obj) => obj.type === 'star');
      if (!star) return 0;
      const starPosition = star.x;
      const playerPosition = scrollOffset;
      const totalDistance = starPosition;
      const progressPercent = Math.min((playerPosition / totalDistance) * 100, 100);
      return Math.round(progressPercent);
    };

    const gameLoop = (time) => {
      if (!lastTime) lastTime = time;
      const delta = time - lastTime;
      lastTime = time;

      accumulatedTime += delta;

      frameCount += 1;
      if (time - lastFpsTime >= 1000) {
        console.log(`FPS: ${frameCount}`);
        frameCount = 0;
        lastFpsTime = time;
      }

      while (accumulatedTime >= targetFrameTime) {
        if (gameStatus === 'playing') {
          update(targetFrameTime);
          setProgress(calculateProgress());
        }
        accumulatedTime -= targetFrameTime;
      }

      draw();

      animationFrameId = requestAnimationFrame(gameLoop);
    };

    if (isImagesReady) {
      draw();
      lastTime = 0;
      animationFrameId = requestAnimationFrame(gameLoop);
    }

    return () => {
      cancelAnimationFrame(animationFrameId);
      canvas.removeEventListener('touchstart', handleTouch);
      canvas.removeEventListener('click', handleTouch);
      window.removeEventListener('resize', handleResize);
      handleResize.cancel();
    };
  }, [gameStatus, failedImages, visibleObjects, jumpVelocity, springJumpVelocity, shatterAnimation, safeMusicSettings, isAudioReady, isImagesReady, gameObjects, resetTrigger, level]);

  const resetGame = () => {
    setGameStatus('loading');
    setShatterAnimation(false);
    setError(null);
    setFailedImages([]);
    setIsAudioReady(false);
    setIsImagesReady(false);
    setVisibleObjects([]);
    setResetTrigger((prev) => prev + 1);
    bgOffsetRef.current = 0;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.src = levelData?.music ? encodeURI(levelData.music) : encodeURI(FALLBACK_MUSIC);
      audioRef.current.load();
    }
    [dieSfxRef, jumpSfxRef, springSfxRef, nexoJumpSfxRef].forEach((sfxRef) => {
      if (sfxRef.current) {
        sfxRef.current.pause();
        sfxRef.current.currentTime = 0;
        sfxRef.current.load();
      }
    });
  };

  const handleNextLevel = (nextLevel) => {
    onGameOver(nextLevel);
  };

  return (
    <div className="relative h-screen w-screen flex justify-center items-center">
      <FPSCounter isEnabled={safeMusicSettings.isFpsCounterEnabled} /> {/* Add FPSCounter */}
      {gameStatus === 'loading' && <LoadingSpinner />}
      <canvas
        ref={canvasRef}
        className={`border-2 border-white bg-black ${gameStatus === 'loading' ? 'hidden' : ''}`}
      />
      {error && (
        <div className="absolute text-white text-center">
          <p>Error: {error}</p>
          <button
            onClick={resetGame}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
          >
            Retry
          </button>
        </div>
      )}
      {gameStatus === 'gameover' && (
        <GameOverModal onReset={resetGame} onMenu={onGameOver} progress={progress} />
      )}
      {gameStatus === 'win' && (
        <WinModal
          onReset={resetGame}
          onMenu={onGameOver}
          onNextLevel={handleNextLevel}
          level={level}
        />
      )}
    </div>
  );
}

Game.propTypes = {
  onGameOver: PropTypes.func.isRequired,
  musicSettings: PropTypes.shape({
    musicVolume: PropTypes.number,
    sfxVolume: PropTypes.number,
    isMusicEnabled: PropTypes.bool,
    isSfxEnabled: PropTypes.bool,
    isFpsCounterEnabled: PropTypes.bool, // Added for type checking
  }),
  level: PropTypes.number,
};

export default Game;